import { useState } from "react";
import { Box, Skeleton, Typography } from "@mui/joy";
import { Camera } from "lucide-react";

import { AuthenticatedImage } from "..";
import { MobileGalleryOverlay } from "./MobileGalleryOverlay";
import { AlbumGroup } from "./types";
import { ImageWithMetadata } from "../../constants/inspectionPhotos";

interface MobileGroupsViewProps {
    groups: AlbumGroup[];
    taskId: string;
    projectId: string;
    loading: boolean;
    onGroupImagesChange: (label: string, images: ImageWithMetadata[]) => void;
}

export function MobileGroupsView(props: MobileGroupsViewProps) {
    const { groups, taskId, projectId, loading, onGroupImagesChange } = props;
    const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
    const activeGroup = groups.find((group) => group.id === activeGroupId) || null;

    return (
        <>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 1.5,
                }}
            >
                {loading
                    ? groups.map((group) => (
                          <Skeleton
                              key={group.id}
                              variant="rectangular"
                              sx={{ aspectRatio: "1/1", borderRadius: "4px" }}
                          />
                      ))
                    : groups.map((group) => (
                          <Box
                              key={group.id}
                              onClick={() => setActiveGroupId(group.id)}
                              sx={{
                                  position: "relative",
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                  aspectRatio: "1/1",
                                  cursor: "pointer",
                                  "&:active": { opacity: 0.85 },
                              }}
                          >
                              <AuthenticatedImage
                                  url={group.coverImage}
                                  isBackground
                                  borderRadius={0}
                                  sx={{
                                      width: "100%",
                                      height: "100%",
                                      backgroundSize: "cover",
                                      backgroundPosition: "center",
                                  }}
                              />

                              <Box
                                  sx={{
                                      position: "absolute",
                                      inset: 0,
                                      background:
                                          "linear-gradient(180deg, rgba(var(--standard-palette-primary-mainChannel) / 0.05) 0%, rgba(var(--standard-palette-primary-darkChannel) / 0.92) 100%)",
                                  }}
                              />

                              {group.images.length > 0 && (
                                  <Box
                                      sx={{
                                          position: "absolute",
                                          top: 8,
                                          right: 8,
                                          bgcolor: "rgba(0,0,0,0.35)",
                                          border: "1px solid rgba(255,255,255,0.15)",
                                          borderRadius: "4px",
                                          px: 0.75,
                                          py: 0.25,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.4,
                                      }}
                                  >
                                      <Camera size={10} color="white" />
                                      <Typography
                                          sx={{
                                              color: "white",
                                              fontSize: "10px",
                                              fontWeight: 700,
                                          }}
                                      >
                                          {group.images.length}
                                      </Typography>
                                  </Box>
                              )}

                              <Box
                                  sx={{
                                      position: "absolute",
                                      inset: 0,
                                      px: 1,
                                      display: "flex",
                                      alignItems: "flex-end",
                                      pb: 1,
                                  }}
                              >
                                  <Typography
                                      sx={{
                                          color: "white",
                                          fontSize: "13px",
                                          fontWeight: 600,
                                          lineHeight: 1.2,
                                          textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                                          display: "-webkit-box",
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: "vertical",
                                          overflow: "hidden",
                                      }}
                                  >
                                      {group.title}
                                  </Typography>
                              </Box>
                          </Box>
                      ))}
            </Box>

            <MobileGalleryOverlay
                group={activeGroup}
                taskId={taskId}
                projectId={projectId}
                onClose={() => setActiveGroupId(null)}
                onImagesChange={(images) => {
                    if (activeGroup) {
                        onGroupImagesChange(activeGroup.label, images);
                    }
                }}
            />
        </>
    );
}
